import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import StrategyGraph from "./component/StrategyGraph";
import MetricsComputations from "./component/MetricsComputations";
function PayoffGraph({
  newltp,
  newSelectedData,
  newAtmIndex,
  newLtptoIV,
  newLotsVal,
  newStep,
  newGreeksData,
  isPosition,
  isDark,
  computedData,
  symbolData,
  selectedSymbol,
  currentOptionsData,
  selectedExpiry
}) {
  const [ltp, setLtp] = useState(0);
  const [isParamsValid, setIsParamsValid] = useState(false);
  const [isDataReceived, setIsDataReceived] = useState(false);
  const [sumGreeksValue, setSumGreeksValue] = useState({
    theta: 0,
    gamma: 0,
    delta: 0,
    vega: 0,
  });
  const [loading, setLoading] = useState(false);
  const [greeksData, setGreeksData] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [dark, setDark] = useState(isDark);
  const [isPositions, setIsPositions] = useState(isPosition);
  const [leastExpiry, setLeastExpiry] = useState('')
  const [lotsVal, setLotsVal] = useState(1); // New state for lotsVal
  const [step, setStep] = useState(1);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const metricsComputations = MetricsComputations();

  // Parse and deduplicate greeksData
  useEffect(() => {
    // console.log('here', newGreeksData)

    if (!newGreeksData) return;

    const parsed = Array.isArray(newGreeksData) ? newGreeksData : JSON.parse(newGreeksData);

    const uniqueGreeksData = parsed.filter(
      (item, index, self) =>
        index === self.findIndex(
          (t) => t.tradingSymbol === item.tradingSymbol && t.price === item.price
        )
    );

    setGreeksData(uniqueGreeksData);
  }, [newGreeksData]);

  // Parse and deduplicate selectedData
  useEffect(() => {
    if (!newSelectedData) return;

    const parsed = Array.isArray(newSelectedData) ? newSelectedData : JSON.parse(newSelectedData);

    const uniqueSelectedData = parsed.filter(
      (item, index, self) =>
        index === self.findIndex(
          (t) => t.tradingSymbol === item.tradingSymbol && t.price === item.price
        )
    );

    setSelectedData(uniqueSelectedData);
  }, [newSelectedData]);

  // Set least expiry when selectedData is ready
  useEffect(() => {
    if (!selectedData || selectedData.length === 0) return;

    const leastExpiryItem = selectedData.reduce((earliest, current) => {
      const earliestDate = new Date(earliest.expiry);
      const currentDate = new Date(current.expiry);
      return currentDate < earliestDate ? current : earliest;
    });

    setLeastExpiry(leastExpiryItem.expiry);
  }, [selectedData]);

  // Set LTP
  useEffect(() => {
    if (newltp !== undefined && newltp !== null) {
      setLtp(newltp);
    }
  }, [newltp]);

  // Fallback: derive LTP from symbolData if missing/zero from parent
  useEffect(() => {
    try {
      const code = selectedSymbol?.code;
      const live = code ? symbolData?.[code]?.ltp : undefined;
      if ((ltp === 0 || ltp === null || ltp === undefined) && live != null && live !== undefined) {
        setLtp(live);
      }
    } catch (_) {}
  }, [symbolData, selectedSymbol, ltp]);

  // Set other independent states
  useEffect(() => {
    setIsParamsValid(true);
    setIsDataReceived(true);
  }, [newSelectedData, newGreeksData]);

  useEffect(() => {
    setIsPositions(isPosition);
  }, [isPosition]);

  useEffect(() => {
    setDark(isDark);
  }, [isDark]);

  useEffect(() => {
    setStep(newStep);
  }, [newStep]);

  useEffect(() => {
    setLotsVal(newLotsVal);
  }, [newLotsVal]);


  const checkParams = () => {
    if (
      ltp !== null &&
      selectedData?.length > 0 &&
      lotsVal !== null &&
      step !== null &&
      greeksData !== null
    ) {
      setIsDataReceived(true);
      setLoading(false)
      return true;
    }
    return false;
  };

  useEffect(() => {
    const answer = checkParams();
    setIsParamsValid(answer);
  }, [ltp, selectedData, lotsVal, step, greeksData]);

  useEffect(() => {
    if (!Array.isArray(greeksData)) {
      return;
    }

    let theta = 0;
    let gamma = 0;
    let delta = 0;
    let vega = 0;
    let rho = 0;

    greeksData.forEach((greek) => {
      theta += parseFloat(greek.theta);
      gamma += parseFloat(greek.gamma);
      delta += parseFloat(greek.delta);
      vega += parseFloat(greek.vega);
      rho += parseFloat(greek.rho);
    });

    gamma = gamma.toFixed(2);
    delta = delta.toFixed(2);
    vega = vega.toFixed(2);
    rho = rho.toFixed(2);
    theta = theta.toFixed(2);

    setSumGreeksValue({
      theta,
      gamma,
      delta,
      vega,
      rho,
    });
  }, [selectedData, greeksData]);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     try {
  //       const metrics = await metricsComputations.computeXandYAxis({
  //         selectedData: selectedData,
  //         ltp: ltp,
  //         step: step,
  //         leastExpiry: leastExpiry,
  //         lotsVal: lotsVal,
  //       });
  //       setComputedData(metrics);
  //       setLoading(false);
  //     } catch (error) {
  //       console.error("Error:", error.message);
  //       setLoading(false);
  //     }
  //   };

  //   if (selectedData?.length > 0) {
  //     fetchData();
  //   } else {
  //     setLoading(false);
  //   }
  // }, [selectedData]);

  const options = {
    title: {
      text: "Metrics Chart",
    },
    xAxis: {
      categories: computedData?.x_axis || [],
    },
    series: [
      {
        name: "Profit/Loss",
        data: computedData?.y_axis || [],
      },
    ],
  };

  if (!isDataReceived) {
    return <div>Loading....,,,,</div>;
  }
  // // console.log("is dark", dark);
  return (
    <div
      className={`flex flex-col gap-[10px] w-full items-center pb-8 bg-P400`}
    >
      {/* Desktop View */}
      <div className="w-full">
        <StrategyGraph
          data={computedData}
          selectedData={selectedData}
          ltp={ltp}
          leastExpiry={leastExpiry}
          options={options}
          greeksData={sumGreeksValue}
          greeksJson={greeksData}
          ltptoIV={newLtptoIV}
          atmIndex={newAtmIndex}
          isParamsValid={isParamsValid}
          setGreeksData={setGreeksData}
          isPositions={isPositions}
          isDark={isDark}
          symbolData={symbolData} // Pass symbolData for OI information
          selectedSymbol={selectedSymbol} // Pass selectedSymbol
          currentOptionsData={currentOptionsData} // Pass currentOptionsData
          selectedExpiry={selectedExpiry} // Pass selectedExpiry
        />
      </div>
    </div>

  );
}

export default PayoffGraph;
